/**
 * transport.ts
 *
 * Connect transport factories.
 *
 * These are functions rather than module-level constants so that importing
 * the service layer has no side effects: no transport is built, and
 * `import.meta.env` is not read, until an app or a test asks for one.
 */
import type { Transport } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import {
  type AuthStoreLike,
  createAuthInterceptor,
  createErrorTransformInterceptor,
  createRetryInterceptor,
} from "@/services/interceptors";

/** Dependencies required to build the authenticated transport. */
export interface AuthTransportDeps {
  getAuthStore: () => AuthStoreLike;
}

/**
 * Builds the unauthenticated transport used for Login, RefreshToken and
 * Logout.
 *
 * Deliberately omits the error-transform interceptor: auth operations need
 * the original Connect error codes to handle failures correctly.
 *
 * @param baseUrl - Backend gRPC base URL.
 * @returns A Connect transport with retry only.
 */
export function createPublicTransport(baseUrl: string): Transport {
  return createConnectTransport({
    baseUrl,
    useBinaryFormat: true,
    interceptors: [createRetryInterceptor()],
  });
}

/**
 * Builds the authenticated transport used by every other service.
 *
 * Interceptor order matters: error transform first, then retry, then auth
 * (which catches 401 and replays the request).
 *
 * @param baseUrl - Backend gRPC base URL.
 * @param deps - Accessor for the auth store.
 * @returns A Connect transport with the full interceptor chain.
 */
export function createAuthTransport(
  baseUrl: string,
  deps: AuthTransportDeps,
): Transport {
  return createConnectTransport({
    baseUrl,
    useBinaryFormat: true,
    interceptors: [
      createErrorTransformInterceptor(),
      createRetryInterceptor(),
      createAuthInterceptor({ getAuthStore: deps.getAuthStore }),
    ],
  });
}
